import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Boswell from './pages/Boswell';
import Accounts from './pages/Accounts';
import Emails from './pages/Emails';
import Threads from './pages/Threads';
import Events from './pages/Events';
import Actions from './pages/Actions';
import Rules from './pages/Rules';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import SettingsAI from './pages/settings/AI';
import SettingsAutomation from './pages/settings/Automation';
import SettingsApprovals from './pages/settings/Approvals';
import SettingsEmail from './pages/settings/Email';
import SettingsOAuth2 from './pages/settings/OAuth2';
import SettingsPlugins from './pages/settings/Plugins';
import Setup from './pages/Setup';
import Plugins from './pages/Plugins';
import Login from './pages/Login';
import Register from './pages/Register';
import ConfigCheck from './components/common/ConfigCheck';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScrollToTop from './components/common/ScrollToTop';
import ProtectedRoute from './components/common/ProtectedRoute';
import { AuthProvider } from './hooks/useAuth.hook';
import useAppStore from './store/appStore';
import { initSocket, on, WS_EVENTS } from './utils/socket';

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

function App() {
  const { initTheme, setAgentStatus, addNotification, addTask, updateTask, removeTask } = useAppStore();

  useEffect(() => {
    // Initialize theme
    initTheme();

    // Initialize WebSocket
    initSocket();

    // Subscribe to WebSocket events
    const unsubscribers = [
      on(WS_EVENTS.AGENT_STATUS, (data) => {
        setAgentStatus({
          ...data,
          isRunning: data.status === 'running',
        });
      }),

      on(WS_EVENTS.EMAIL_PROCESSED, ({ email }) => {
        // Silent processing - no notification spam
      }),

      on(WS_EVENTS.SYNC_STARTED, ({ accountId, accountName }) => {
        // Create task for sync operation
        addTask({
          id: `sync-${accountId}`,
          title: `Syncing ${accountName || `Account ${accountId}`}`,
          type: 'sync',
          accountId,
          cancellable: false,
        });
      }),

      on(WS_EVENTS.SYNC_COMPLETED, ({ accountId, accountName, emailsFound }) => {
        // Remove task
        removeTask(`sync-${accountId}`);

        // Always add notification for completed sync
        addNotification({
          type: 'success',
          title: 'Sync Completed',
          message: emailsFound > 0
            ? `Found ${emailsFound} new email${emailsFound === 1 ? '' : 's'}`
            : 'No new emails found',
        });

        // Invalidate email queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['emails'] });
        queryClient.invalidateQueries({ queryKey: ['email-stats'] });
      }),

      on(WS_EVENTS.SYNC_ERROR, ({ accountId, error }) => {
        // Remove task
        removeTask(`sync-${accountId}`);

        // Add error notification
        addNotification({
          type: 'error',
          title: 'Sync Error',
          message: error,
        });
      }),
    ];

    // Cleanup
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [initTheme, setAgentStatus, addNotification, addTask, updateTask, removeTask]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ConfigCheck>
              <Routes>
                {/* Auth routes - protected from authenticated users */}
                <Route path="/login" element={
                  <ProtectedRoute requireAuth={false}>
                    <Login />
                  </ProtectedRoute>
                } />
                <Route path="/register" element={
                  <ProtectedRoute requireAuth={false}>
                    <Register />
                  </ProtectedRoute>
                } />

                {/* Setup route - always accessible (for local mode) */}
                <Route path="/setup" element={<Setup />} />

                {/* Protected app routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="boswell" element={<Boswell />} />
                  <Route path="accounts" element={<Accounts />} />
                  <Route path="emails" element={<Emails />} />
                  <Route path="threads" element={<Threads />} />
                  <Route path="events" element={<Events />} />
                  <Route path="actions" element={<Actions />} />
                  <Route path="rules" element={<Rules />} />
                  <Route path="plugins" element={<Plugins />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="settings" element={<Settings />}>
                    <Route index element={<Navigate to="ai" replace />} />
                    <Route path="ai" element={<SettingsAI />} />
                    <Route path="automation" element={<SettingsAutomation />} />
                    <Route path="approvals" element={<SettingsApprovals />} />
                    <Route path="email" element={<SettingsEmail />} />
                    <Route path="oauth2" element={<SettingsOAuth2 />} />
                    <Route path="plugins" element={<SettingsPlugins />} />
                  </Route>
                </Route>

                {/* Fallback redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ConfigCheck>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
